import { datadogRum } from '@datadog/browser-rum';
import Cookies from 'js-cookie';
import { call, delay, race, SagaGenerator } from 'typed-redux-saga';
import userflow from 'userflow.js';
import {
  FeatureKeys,
  sessionApiClient,
  usersApiClient,
} from '@owl-frontend/api-client';
import type { User } from '@owl-frontend/api-client/src/users/interface';
import { asyncActionStateMatchers, invoke } from '@owl-frontend/redux';
import { createLogger } from '@owl-lib/logger';
import sessionSlice from './interface';

const logger = createLogger(__filename);

if (RUM_APPLICATION_ID && RUM_CLIENT_TOKEN) {
  datadogRum.init({
    env: ENV,
    version: __version,
    applicationId: RUM_APPLICATION_ID,
    clientToken: RUM_CLIENT_TOKEN,
    site: 'datadoghq.com',
    service: 'phoenix',
    sessionSampleRate: 100,
    sessionReplaySampleRate: 100, // if not included, the default is 100
    trackResources: true,
    trackLongTasks: true,
    trackUserInteractions: true,
    defaultPrivacyLevel: 'mask-user-input',
  });

  const tenantHostName = window.location.hostname.split('.')[0];

  datadogRum.addRumGlobalContext('tenant', {
    name: tenantHostName,
  });

  datadogRum.setGlobalContextProperty('viewport', {
    width: window.screen.width,
    height: window.screen.height,
  });

  datadogRum.startSessionReplayRecording();
}

if (USERFLOW_KEY) {
  userflow.init(USERFLOW_KEY);
}

function* logout() {
  datadogRum.stopSessionReplayRecording();
  yield* call(sessionApiClient.session.logout);
}

const constructPayloadChecksum = async (data: {
  username: string;
  payload: string;
}): Promise<string> => {
  const { username, payload } = data;
  const encoder = new TextEncoder();
  const encoded = encoder.encode(`${username}${payload}`);
  const hashBuffer = await crypto.subtle.digest('SHA-256', encoded);
  return Array.from(new Uint8Array(hashBuffer))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
};

function* current(): SagaGenerator<User | undefined> {
  const { sessionCurrent, sessionTimeout } = yield* race({
    sessionCurrent: call(usersApiClient.users.current),
    sessionTimeout: delay(10_000), // 10 seconds
  });

  if (sessionTimeout) {
    throw new Error('TIMED_OUT');
  }

  if (RUM_APPLICATION_ID && RUM_CLIENT_TOKEN && sessionCurrent) {
    datadogRum.setUser({
      id: sessionCurrent.username,
      name: sessionCurrent.name,
      email: sessionCurrent.email,
    });
  }

  const checksum = Cookies.get('checksum');

  if (!checksum) {
    throw new Error('Missing checksum!');
  }

  const username = Cookies.get('username');

  if (!username) {
    throw new Error('Unable to verify response');
  }

  const payloadChecksum = yield* call(constructPayloadChecksum, {
    username,
    payload: JSON.stringify(sessionCurrent),
  });

  if (payloadChecksum !== checksum) {
    throw new Error(`Checksums do not match! ${payloadChecksum}, ${checksum}`);
  }

  return sessionCurrent;
}

const slice = sessionSlice
  .addAsyncSagas({
    current,
    fetchFeatures: invoke(sessionApiClient.features.get),
    logEvent: invoke(sessionApiClient.session.logEvent),
    logout,
  })
  .addReducers({
    'current/fulfilled': (state, action) => {
      if (action.payload) {
        const { username, name, email, role } = action.payload;

        if (USERFLOW_KEY) {
          userflow
            .identify(username, {
              name,
              email,
              role,
            })
            .then((r) => {
              logger.info('Set userflow identity: %o', r);
              return;
            })
            .catch((e) => {
              logger.error('Failed to set userflow identity: %o', e);
            });
        }
      }

      state.current = action.payload;
    },
    'fetchFeatures/fulfilled': (state, action) => {
      state.features = action.payload.reduce<{ [k in FeatureKeys]?: true }>(
        (acc, curr) => ({
          ...acc,
          [curr]: true,
        }),
        {}
      );
    },
  });
export const actions = slice.actions;
export default slice.addExtra(asyncActionStateMatchers(actions).all());
